import { useEffect, useState, useRef } from 'react';
import { useSession, useNextgenRouter, useTranslation } from '@nextgen-web-framework/all';
import { StorageService } from '@nextgen-web-framework/core';
import {
  AmwayValue,
  CartConstantsEnum,
  CartEntryDetails,
  CartEvents,
  CartEventsEnum,
  CartEventsVersionsEnum,
  ErrorAPI,
  InitialCartListState,
  ListEntry,
  ListExceptionDetail,
  ListResponse,
  ListResponseType,
  ListType,
  getBundleEntriesAndChildPromoitions,
  getStockStatus,
  myListApi,
  updateMyList,
  getExceptionProductsinList,
  UserListResponse,
  PublishWishlistEvent,
  CartRoutePaths,
  MyShopStorageConstant,
  getVariantName,
} from '@commerce-cart-webcomponents/sdk';
import { ProductCartEntryComponent } from '@commerce-product-webcomponents/ui-composite';
import type { AppState } from '@nextgen-web-framework/core';

import { useDispatch, useSelector } from 'react-redux';
import {
  useCartNotification,
  CartNotificationTheme,
  TOAST_TIMEOUT,
  AllCustomListContext,
  ProductFlag,
  ListSummaryLoader,
  ListTotal,
  AccountRegistrationBanner,
  useFeatureFlag,
} from '@commerce-cart-webcomponents/ui';
import { ErrorCard, Errors } from '@commerce-webcomponents/ui';
import { AdvanceShareCodeResponse } from '@commerce-webcomponents/sdk';
import { ProductType, VariantDimension } from '@commerce-product-webcomponents/sdk';
import { errorImagePath } from '../../../cart-container/types/enums';
import { MyListItemsBannerLoader, MyListItemsLoader } from './my-list-items-loader/MyListItemsLoader';
import { EmptyList } from './empty-list/EmptyList';
import { ListBanner } from './list-banner/ListBanner';
import styles from './MyListItems.module.scss';

// CMS_CONTENT_TYPE

export const MyListItems = () => {
  const t = useTranslation();
  const dispatch = useDispatch();
  const router = useNextgenRouter();
  const subscriptionIds = useRef<string[]>();
  const [listId, setListId] = useState(router.params?.listId ?? '');
  const { myList }: InitialCartListState = useSelector((state: AppState) => state.commerceMyList);
  const [listData, setListData] = useState<ListResponse>(myList?.listId === listId ? myList : {});
  const [showListRemoved, setShowListRemoved] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const [showSummaryLoader, setShowSummaryLoader] = useState(false);
  const [addCartToListDrawer, setAddCartToListDrawer] = useState(false);
  const { isABO } = useSession();
  const isWishlist = listData.listType === ListType.Wishlist;
  const isEmpty = listData?.payLoad?.entries?.length === 0;
  const [invalidFlag, setInvalidFlag] = useState<boolean>(false);
  const [userRegistrationFee, setUserRegistrationFee] = useState(0);
  const configs = ConfigService.getConfigs();
  const assetsBasePath = configs.NEXT_ASSETS_BASE_PATH as string;
  const [showErrorCard, setShowErrorCard] = useState(false);
  const { hideBVFlag } = useFeatureFlag();

  const { createToast, deleteToast } = useCartNotification('addToList');
  const handleListResponse = (res: ListResponse) => {
    setListData(res);
    res.listType === ListType.Wishlist && dispatch(updateMyList(res));
  };

  const productsRemovedText = t('cart.listItems.productsRemovedfromList');
  const invalidProductsRemovedText = t('cart.listItems.invalidProductsRemovedfromList');

  const undoText = t('cart.cartLineItem.undo');

  const sharingRestrictedFlag = t('cart.myListItems.sharingRestrictedFlag');

  useEffect(() => {
    const shareCode = StorageService?.getLocalStorage()?.retrieve(
      MyShopStorageConstant.SHARECODEATTRIBUTES,
    ) as AdvanceShareCodeResponse;
    listId &&
      myListApi
        .getListById({
          listId: listId as string,
          accountId: shareCode?.accountId,
          userId: shareCode?.profileId,
        })
        .then((res: ListResponseType) => {
          handleListResponse(res);
          res.listType === ListType.Wishlist &&
            StorageService.getLocalStorage().store(CartConstantsEnum.ListId, listId as string);
        })
        .catch((...errorProps: ErrorAPI[]) => {
          const errorDetails = errorProps[0]?.responseBody?.detail;
          if (
            errorDetails === ListExceptionDetail.ListNotFound ||
            errorDetails === ListExceptionDetail.PrivateListType
          ) {
            setShowListRemoved(true);
            return;
          }
          setShowErrorCard(true);
        })
        .finally(() => setShowLoader(false));
  }, [listId, router.params?.locale]);

  useEffect(() => {
    const showLoaderEvent = CartEvents.subscribeEvents({
      topics: [CartEventsEnum.ShowLoader],
      version: CartEventsVersionsEnum['1.0.0'],
      cb: () => {
        setShowSummaryLoader(true);
      },
    });

    const hideLoaderEvent = CartEvents.subscribeEvents({
      topics: [CartEventsEnum.HideLoader],
      version: CartEventsVersionsEnum['1.0.0'],
      cb: () => {
        setShowSummaryLoader(false);
      },
    });

    subscriptionIds.current = [...hideLoaderEvent, ...showLoaderEvent];

    return () => {
      CartEvents.unsubscribe(subscriptionIds.current);
    };
  }, []);

  useEffect(() => {
    router.params?.listId && setListId(router.params?.listId as string);
  }, [router.params]);

  const updateCustomList = (value: ListResponse) => {
    setListData(value);
  };

  const addToList = (listEntry: ListEntry, toastId: string, res: ListResponse) => {
    myListApi
      .addToList([
        {
          version: res?.version as number,
          listId: res?.listId as string,
          entries: [listEntry],
        },
      ])
      .then((res: UserListResponse) => {
        deleteToast(toastId);
        updateCustomList(res?.lists?.[0] || {});
        PublishWishlistEvent({
          cartEvent: CartEventsEnum.AddToWishlist,
          listEntry: listEntry,
        });
      });
  };

  const removeEntryfromList = (entryId: number, entryIdArray: number[] = [], isInvalid = false) => {
    setShowSummaryLoader(true);
    const listEntry = listData?.payLoad?.entries?.find((entry: ListEntry) => entry.entryId == entryId);
    myListApi
      .removeEntryFromList({
        entryId: entryIdArray.length !== 0 ? entryIdArray : [entryId],
        listId: listData?.listId,
      })
      .then((res: ListResponse) => {
        updateCustomList(res);
        handleListResponse(res);
        PublishWishlistEvent({
          cartEvent: CartEventsEnum.RemoveWishlistEntry,
          listEntry: listEntry,
        });
        createToast(
          {
            theme: isInvalid ? CartNotificationTheme.YELLOW : CartNotificationTheme.SUCCESS,
            content: isInvalid ? invalidProductsRemovedText : productsRemovedText,
            textlink: isInvalid ? '' : undoText,
            timeOut: TOAST_TIMEOUT,
            icon: 'check-circle',
          },
          (id: string) => addToList(listEntry as ListEntry, id, res),
        );
      })
      .finally(() => setShowSummaryLoader(false));
  };

  useEffect(() => {
    if (listData?.payLoad?.entries && !invalidFlag) {
      const { invalidProducts } = getExceptionProductsinList(listData?.payLoad?.entries);

      if (invalidProducts !== undefined && invalidProducts.length !== 0) {
        setInvalidFlag(true);
        const removedEntriesPayload = invalidProducts
          .map((entry) => entry.entryId)
          .filter((id): id is number => id !== undefined);

        removeEntryfromList(0, removedEntriesPayload, true);
      }
    }
  }, [listData]);

  const receivedRegistrationFees = (regFee: number) => {
    setUserRegistrationFee(regFee);
  };

  const showQuantityStepper = () => (listData.isReceiver ? false : listData.listType !== ListType.Wishlist);

  if (showListRemoved) {
    return <EmptyList listRemoved={true} />;
  }

  if (showErrorCard) {
    return (
      <ErrorCard
        title={t('cart.cartContainer.errorTitle')}
        description={t('cart.cartContainer.errorDescription')}
        buttonLabel={t('cart.mylistItems.goBack')}
        imageUrl={`${assetsBasePath}/${errorImagePath}`}
        onActionClick={() => {
          const shareCode = StorageService.getLocalStorage().retrieve(
            MyShopStorageConstant.SHARECODEATTRIBUTES,
          ) as AdvanceShareCodeResponse;
          shareCode ? router?.push(CartRoutePaths.HOME) : router?.back();
        }}
        type={Errors.General}
      />
    );
  }
  return (
    <AllCustomListContext.Provider value={{ customList: listData, updateCustomList }}>
      {showLoader && !isWishlist ? (
        <MyListItemsBannerLoader />
      ) : (
        <ListBanner showLoaderCB={(show) => setShowLoader(show)} listDetails={listData} />
      )}
      {showLoader ? (
        <MyListItemsLoader isWishlist={isWishlist} />
      ) : (
        <div
          className={`${styles['myList-items']} ${isWishlist && styles['myList-items-wishlist']} ${
            isEmpty && styles['myList-items-wishlist-empty']
          }`}
        >
          <div className={styles['myList-line-item']}>
            {isEmpty ? (
              <EmptyList isWishlist={isWishlist} />
            ) : (
              <>
                <AccountRegistrationBanner isListBanner={true} registrationFeesCb={receivedRegistrationFees} />
                {listData?.payLoad?.entries?.map((entry: ListEntry) => {
                  const { totalPrice, product } = entry;
                  const { pointValue, businessVolume } = totalPrice?.amwayValue ?? ({} as AmwayValue);
                  return (
                    <div className={styles['myList-line-item-entry']} key={entry.entryId}>
                      <ProductCartEntryComponent
                        onRemoveProduct={() => entry.entryId && removeEntryfromList(entry.entryId)}
                        showPropertiesCollection={{
                          showQtyStepper: showQuantityStepper(),
                          showCloseCta: false,
                          showQuantityLabel: listData.isReceiver,
                          showCancelIcon: !listData.isReceiver,
                          showDispositionLinkCTA: !listData.isReceiver,
                        }}
                        data={{
                          productId: product.id,
                          bundle: getBundleEntriesAndChildPromoitions(entry as CartEntryDetails)?.bundleEntries,
                          link: product.cartIconPublicUrl ?? '',
                          alt: product.cartIconAltText as string,
                          brandName: product.brand as string,
                          productName: product.name ?? '',
                          productPrice: totalPrice?.value as number,
                          productSize: product.copyServingSize as string,
                          pointValue: String(isABO() && (pointValue || 0)),
                          businessVolume: String(isABO() && !hideBVFlag && (businessVolume || 0)),
                          variantName: getVariantName(product.variantDimensions as VariantDimension[]),
                          productType: product.type,
                          derivedAvailabilityStatus: getStockStatus(entry),
                          firstAvailableFutureDate:
                            product.firstAvailableFutureDate && String(product.firstAvailableFutureDate),
                          showPrefix: false,
                          showSubText: true,
                          itemDisposition: (product?.type === ProductType.Bundle
                            ? product?.derivedAvailabilityStatus
                            : product?.itemDisposition) as string,
                          entryId: entry?.entryId,
                          productQuantity: entry.quantity,
                          items: [],
                        }}
                        cartBaseurl={''}
                      />
                      {entry.product.flags?.restrictFromSharing && (
                        <ProductFlag iconName="ban" text={sharingRestrictedFlag} />
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
          {showSummaryLoader && !addCartToListDrawer ? (
            <ListSummaryLoader className={styles['myList-line-item-loader']} />
          ) : (
            <>{!isEmpty && <ListTotal cartDrawerCB={setAddCartToListDrawer} registrationFee={userRegistrationFee} />}</>
          )}
        </div>
      )}
    </AllCustomListContext.Provider>
  );
};
